import type { DeleteCustomDatasourceMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useTRPC } from '@/utils/trpc';

/**
 * Maps the tRPC (void) delete response to the GraphQL mutation shape. The
 * legacy `delete_custom_datasource_by_pk` returned the deleted row's `Id`;
 * the tRPC service resolves void, so we surface `null` (the caller — the
 * DeleteModal — does not read the returned Id).
 */
const mapTrpcResponseToGraphQL = (): DeleteCustomDatasourceMutation => {
  return {
    delete_custom_datasource_by_pk: null,
  };
};

export const useDeleteCustomDatasourceTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.customDatasource.delete.mutationOptions({
      // Mirrors the legacy Apollo `evictField(cache, 'custom_datasource')` +
      // `evictField(cache, 'custom_datasource_by_pk')` cache eviction.
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey:
              trpc.frontend.customDatasource.customDatasources.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey:
              trpc.frontend.customDatasource.customDatasourceById.queryKey(),
          }),
        ]);
      },
    }),
  });

  return {
    deleteCustomDatasource: async (variables: {
      Id: string;
    }): Promise<DeleteCustomDatasourceMutation> => {
      await mutation.mutateAsync({ Id: variables.Id });

      return mapTrpcResponseToGraphQL();
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
